(ns frontend.ui.page.lp-report-editor.step-2-data-gathering.event
  (:require
   [clojure.string :as string]
   [clojure.edn :as edn]
   [common.localization :as loc]
   [common.time :as c-time]
   [common.util :as cu]
   [frontend.controller.websocket.event :as ws]
   [frontend.ui.component.file-input.event :as file-event]
   [frontend.ui.page.lp-report-editor.event :as editor-event]
   [frontend.ui.page.lp-report-editor.step-2-data-gathering.sub :as sub]
   [frontend.ui.page.lp-report-editor.sub :as editor-sub]
   [frontend.view.modals.error-message.view :as error-message]
   [frontend.view.modals.events :as modals]
   [potpuri.core :as p]
   [re-frame.core :as rf]))

(defn- private-material-bulk-upload-file->state
  [file]
  {:id            (str (random-uuid))
   :name          (.-name file)
   :file          file
   :size          (.-size file)
   :last-modified (.-lastModified file)})

(defn- normalize-private-material-import-id
  [value]
  (some-> value
          (cu/str-trim-lcase)
          (not-empty)))

(defn- effective-private-material-import-id
  [{:keys [private-file-import-id common/external-id]}]
  (or (normalize-private-material-import-id private-file-import-id)
      (normalize-private-material-import-id external-id)))

(defn- normalize-private-material-name-match
  [value]
  (some-> value
          (string/lower-case)
          (string/replace #"\[[^\[\]]+\]" " ")
          (string/replace #"\.[^.]+$" "")
          (string/replace #"[_\-]+" " ")
          (string/replace #"[^a-z0-9\s]" " ")
          (string/replace #"\s+" " ")
          (cu/str-trim)
          (not-empty)))

(defn- parse-private-material-import-id
  [filename]
  (let [filename (or filename "")
        bracket-matches (re-seq #"\[([^\[\]]+)\]" filename)
        underscored-matches (re-seq #"__([^_\.]+)(?:_|\.|$)" filename)
        matches (or (when (seq bracket-matches)
                      bracket-matches)
                    underscored-matches)]
    (when (= 1 (count matches))
      (-> matches
          (first)
          (second)
          (cu/str-trim)
          (not-empty)))))

(defn- single-auto-match
  [commitment-ids]
  (let [commitment-ids (->> commitment-ids
                            (distinct)
                            (vec))]
    (when (= 1 (count commitment-ids))
      commitment-ids)))

(defn- name-match-commitment-ids
  [normalized-filename commitment-options]
  (when (seq normalized-filename)
    (let [matching-ids (->> commitment-options
                            (keep (fn [{:keys [commitment-id investor-name]}]
                                    (let [normalized-investor-name
                                          (normalize-private-material-name-match investor-name)]
                                      (when (and (seq normalized-investor-name)
                                                 (string/includes? normalized-filename
                                                                   normalized-investor-name))
                                        commitment-id))))
                            (distinct)
                            (vec))]
      (single-auto-match matching-ids))))

(defn- with-private-material-matches
  [files commitment-options]
  (let [commitment-by-import-id (->> commitment-options
                                     (reduce (fn [acc {:keys [commitment-id] :as option}]
                                               (if-let [normalized-id
                                                        (effective-private-material-import-id option)]
                                                 (update acc normalized-id (fnil conj []) commitment-id)
                                                 acc))
                                             {}))]
    (->> files
         (mapv (fn [{:keys [name selected-commitment-ids] :as file}]
                 (let [parsed-import-id (parse-private-material-import-id name)
                       normalized-import-id (normalize-private-material-import-id
                                             parsed-import-id)
                       matched-commitment-ids (get commitment-by-import-id
                                                   normalized-import-id)
                       normalized-name (normalize-private-material-name-match name)
                       matched-by-name-ids (when-not parsed-import-id
                                             (name-match-commitment-ids
                                              normalized-name
                                              commitment-options))
                       match-source (cond
                                      (seq matched-commitment-ids) :import-id
                                      (seq matched-by-name-ids) :investor-name
                                      :else nil)]
                   (assoc file
                          :parsed-import-id parsed-import-id
                          :match-source (or (:match-source file)
                                            match-source)
                          :selected-commitment-ids (or (not-empty selected-commitment-ids)
                                                       matched-commitment-ids
                                                       matched-by-name-ids
                                                       []))))))))

(defn- file-selected-commitment-ids
  [{:keys [selected-commitment-id selected-commitment-ids]}]
  (cond
    (seq selected-commitment-ids)
    selected-commitment-ids

    selected-commitment-id
    [selected-commitment-id]

    :else
    []))

(defn- update-private-material-bulk-upload-file
  [db file-id update-fn]
  (update-in db
             (conj sub/private-material-bulk-upload-path :files)
             (fn [files]
               (mapv (fn [{:keys [id] :as file}]
                       (if (= id file-id)
                         (update-fn file)
                         file))
                     files))))

(defn- advance-private-material-bulk-upload
  [db]
  (let [{:keys [cancel-requested? master-report-id stop-processing-dialog-visible?]}
        (get-in db sub/private-material-bulk-upload-path)]
    (when-not stop-processing-dialog-visible?
      [:dispatch
       (if cancel-requested?
         [::complete-bulk-upload master-report-id]
         [::start-next-bulk-upload])])))

(defn- private-material-bulk-upload-files
  [db]
  (get-in db (conj sub/private-material-bulk-upload-path :files)))

(defn- next-private-material-bulk-upload-file
  [files]
  (->> files
       (filter (fn [{:keys [selected-commitment-id status]}]
                 (and selected-commitment-id
                      (= status :pending))))
       (first)))

(defn- selected-private-material-bulk-upload-commitment-ids
  [files]
  (->> files
       (mapcat file-selected-commitment-ids)
       (set)))

(defn- expand-private-material-bulk-upload-files
  [files]
  (->> files
       (mapcat (fn [{:keys [id] :as file}]
                 (->> (file-selected-commitment-ids file)
                      (map-indexed
                       (fn [idx commitment-id]
                         (-> file
                             (assoc :id (str id "-" idx "-" commitment-id))
                             (assoc :source-file-id id)
                             (assoc :selected-commitment-id commitment-id)
                             (dissoc :selected-commitment-ids)))))))
       (vec)))

(defn- private-material-bulk-upload-cleanup-commitments
  [files commitment-options]
  (let [selected-commitment-ids (selected-private-material-bulk-upload-commitment-ids files)]
    (->> commitment-options
         (filter (fn [{:keys [commitment-id materials]}]
                   (and (or (empty? selected-commitment-ids)
                            (contains? selected-commitment-ids commitment-id))
                        (seq materials))))
         (vec))))

(defn- private-material-bulk-upload-removals
  [files commitment-options remove-existing-attachment-ids]
  (let [cleanup-commitments (private-material-bulk-upload-cleanup-commitments
                             files
                             commitment-options)
        removal-ids (set remove-existing-attachment-ids)]
    (->> cleanup-commitments
         (mapcat (fn [{:keys [commitment-id materials]}]
                   (->> materials
                        (keep (fn [attachment]
                                (when (contains? removal-ids (:db/id attachment))
                                  {:attachment-id   (:db/id attachment)
                                   :attachment-name (:attachment/filename attachment)
                                   :commitment-id   commitment-id}))))))
         (vec))))

(defn- private-material-bulk-upload-error-message
  [error]
  (or (:message error)
      (get-in error [:body :message])
      :private-material-bulk-upload-cleanup-failed))

(rf/reg-event-fx
 ::reset
 (fn [{db :db} _]
   {:db (-> db
            (assoc-in sub/gathering-mode-path nil)
            (assoc-in sub/gathering-holding-path nil)
            (p/dissoc-in sub/private-material-bulk-upload-path))}))

(rf/reg-event-fx
 ::clear-notification
 (fn [{db :db} [_ _report-id]]
   {:db (-> db
            (p/dissoc-in [::sub/fund-text-rejected?])
            (p/dissoc-in [::sub/fund-text-submitted?])
            (p/dissoc-in [::sub/fund-data-rejected?])
            (p/dissoc-in [::sub/fund-data-submitted?])
            (p/dissoc-in [::sub/holding-text-rejected?])
            (p/dissoc-in [::sub/holding-text-submitted?])
            (p/dissoc-in [::sub/holding-data-rejected?])
            (p/dissoc-in [::sub/holding-data-submitted?]))}))

(rf/reg-event-fx
 ::reset-if-different-report
 (fn [{db :db} [_ new-report-id]]
   (if (not= new-report-id (:db/id (get-in db editor-sub/report-path)))
     {:db (assoc-in db sub/gathering-path nil)}
     (do
       (js/console.log "NO RESET Gathering mode : " new-report-id
                       " <> " (:db/id (get-in db editor-sub/report-path)))
       {}))))

(rf/reg-event-fx
 ::change-tab
 (fn [{db :db} [_ tab-key]]
   {:db (assoc-in db sub/selected-tab-path tab-key)}))

(rf/reg-event-fx
 ::edit-holding-report
 (fn [{db :db} [_ holding-report]]
   {:db (-> db
            (assoc-in sub/gathering-mode-path :holding-report-editor)
            (assoc-in sub/gathering-holding-path (:db/id holding-report)))}))

(rf/reg-event-fx
 ::start-upload
 (fn [{db :db} [_ files]]
   (let [existing-uploads (get-in db sub/uploads-path)]
     {:db (-> db
              (assoc-in sub/uploads-path
                        (concat []
                                existing-uploads
                                (if (seq? files)
                                  (->> files
                                       (map (fn [file]
                                              {:name          (.-name file)
                                               :file          file
                                               :size          (.-size file)
                                               :last-modified (.-lastModified file)
                                               :percentage    0
                                               :status        :uploading})))
                                  [{:name          (.-name files)
                                    :file          files
                                    :size          (.-size files)
                                    :last-modified (.-lastModified files)
                                    :percentage    0
                                    :status        :uploading}]))))})))

(rf/reg-event-fx
 ::end-upload
 (fn [{db :db} [_ file]]
   (let [updated-files (->> (get-in db sub/uploads-path)
                            (mapv (fn [f]
                                    (if-not (= (:file f) file)
                                      f
                                      (if (= (:status f) :error)
                                        f
                                        (assoc f
                                               :status :complete
                                               :percentage 100))))))]
     {:db (assoc-in db sub/uploads-path updated-files)})))

(rf/reg-event-fx
 ::hide-uploads
 (fn [{db :db} _]
   {:db (p/dissoc-in db sub/uploads-path)}))

(rf/reg-event-fx
 ::open-bulk-upload
 (fn [{db :db} [_ master-report-id report-id feeder?]]
   {:db (assoc-in db
                  sub/private-material-bulk-upload-path
                  {:master-report-id master-report-id
                   :report-id        report-id
                   :feeder?          feeder?
                   :step             :upload
                   :files            []
                   :cleanup-source-step nil
                   :cleanup-mode    nil
                   :progress-mode    nil
                   :progress-total-files 0
                   :progress-total-removals 0
                   :operation-in-flight? false
                   :cancel-requested? false
                   :stop-processing-dialog-visible? false
                   :cleanup-error-message nil
                   :remove-existing-attachment-ids #{}
                   :pending-removals []})}))

(rf/reg-event-fx
 ::open-bulk-upload-cleanup-step
 (fn [{db :db} [_ source-step]]
   {:db (-> db
            (assoc-in (conj sub/private-material-bulk-upload-path :step) :cleanup)
            (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-source-step) source-step)
            (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-mode) :cleanup-only)
            (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-error-message) nil)
            (assoc-in (conj sub/private-material-bulk-upload-path :remove-existing-attachment-ids) #{}))}))

(rf/reg-event-fx
 ::open-bulk-upload-stop-dialog
 (fn [{db :db} _]
   (when-not (get-in db
                     (conj sub/private-material-bulk-upload-path :cancel-requested?))
     {:db (assoc-in db
                    (conj sub/private-material-bulk-upload-path :stop-processing-dialog-visible?)
                    true)})))

(rf/reg-event-fx
 ::close-bulk-upload-stop-dialog
 (fn [{db :db} _]
   (let [updated-db (assoc-in db
                              (conj sub/private-material-bulk-upload-path
                                    :stop-processing-dialog-visible?)
                              false)]
     {:db updated-db
      :fx (cond-> []
            (and (= :progress (get-in updated-db
                                      (conj sub/private-material-bulk-upload-path :step)))
                 (not (get-in updated-db
                              (conj sub/private-material-bulk-upload-path
                                    :operation-in-flight?))))
            (conj [:dispatch [::start-next-bulk-upload]]))})))

(rf/reg-event-fx
 ::confirm-bulk-upload-stop
 (fn [{db :db} _]
   (let [updated-db (-> db
                        (assoc-in (conj sub/private-material-bulk-upload-path :cancel-requested?) true)
                        (assoc-in (conj sub/private-material-bulk-upload-path
                                        :stop-processing-dialog-visible?)
                                  false))
         master-report-id (get-in updated-db
                                  (conj sub/private-material-bulk-upload-path :master-report-id))]
     {:db updated-db
      :fx (cond-> []
            (not (get-in updated-db
                         (conj sub/private-material-bulk-upload-path :operation-in-flight?)))
            (conj [:dispatch [::complete-bulk-upload master-report-id]]))})))

(rf/reg-event-fx
 ::close-bulk-upload
 (fn [{db :db} _]
   {:db (p/dissoc-in db sub/private-material-bulk-upload-path)}))

(rf/reg-event-fx
 ::add-bulk-upload-file
 (fn [{db :db} [_ file]]
   (when file
     {:db (update-in db
                     (conj sub/private-material-bulk-upload-path :files)
                     (fnil conj [])
                     (private-material-bulk-upload-file->state file))})))

(rf/reg-event-fx
 ::remove-bulk-upload-file
 (fn [{db :db} [_ file-id]]
   {:db (update-in db
                   (conj sub/private-material-bulk-upload-path :files)
                   (fn [files]
                     (->> files
                          (remove (fn [{:keys [id]}]
                                    (= id file-id)))
                          (vec))))}))

(rf/reg-event-fx
 ::next-bulk-upload-step
 (fn [{db :db} [_ commitment-options]]
   (let [matched-files (-> db
                           (get-in (conj sub/private-material-bulk-upload-path :files))
                           (with-private-material-matches commitment-options))]
     {:db (-> db
              (assoc-in (conj sub/private-material-bulk-upload-path :step) :review)
              (assoc-in (conj sub/private-material-bulk-upload-path :files) matched-files))})))

(rf/reg-event-fx
 ::back-bulk-upload-step
 (fn [{db :db} _]
   (let [current-step (get-in db
                              (conj sub/private-material-bulk-upload-path :step))
         cleanup-source-step (get-in db
                                     (conj sub/private-material-bulk-upload-path
                                           :cleanup-source-step))]
     {:db (-> db
              (assoc-in (conj sub/private-material-bulk-upload-path :step)
                        (case current-step
                          :cleanup (or cleanup-source-step :review)
                          :review :upload
                          :upload :upload
                          current-step))
              (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-mode)
                        (when-not (= current-step :cleanup)
                          (get-in db (conj sub/private-material-bulk-upload-path :cleanup-mode))))
              (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-source-step)
                        (when-not (= current-step :cleanup)
                          cleanup-source-step)))})))

(rf/reg-event-fx
 ::select-bulk-upload-commitment
 (fn [{db :db} [_ file-id commitment-ids]]
   {:db (update-in db
                   (conj sub/private-material-bulk-upload-path :files)
                   (fn [files]
                     (mapv (fn [{:keys [id] :as file}]
                             (if (= id file-id)
                               (assoc file
                                      :match-source (when (seq commitment-ids)
                                                      :manual)
                                      :selected-commitment-ids
                                      (vec commitment-ids))
                               file))
                           files)))}))

(rf/reg-event-fx
 ::finish-bulk-upload-review
 (fn [{db :db} [_ commitment-options]]
   {:db (-> db
            (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-mode)
                      :pre-import)
            (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-source-step)
                      :review))
    :fx [[:dispatch [::start-bulk-upload-progress commitment-options]]]}))

(rf/reg-event-fx
 ::toggle-bulk-upload-existing-attachment
 (fn [{db :db} [_ attachment-id]]
   {:db (-> db
            (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-error-message) nil)
            (update-in (conj sub/private-material-bulk-upload-path :remove-existing-attachment-ids)
                       (fn [attachment-ids]
                         (let [attachment-ids (or attachment-ids #{})]
                           (if (contains? attachment-ids attachment-id)
                             (disj attachment-ids attachment-id)
                             (conj attachment-ids attachment-id))))))}))

(rf/reg-event-fx
 ::remove-all-bulk-upload-existing-attachments
 (fn [{db :db} [_ commitment-options]]
   (let [attachment-ids (->> (private-material-bulk-upload-cleanup-commitments
                              (private-material-bulk-upload-files db)
                              commitment-options)
                             (mapcat :materials)
                             (map :db/id)
                             (set))]
     {:db (-> db
              (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-error-message) nil)
              (assoc-in (conj sub/private-material-bulk-upload-path :remove-existing-attachment-ids)
                        attachment-ids))})))

(rf/reg-event-fx
 ::start-bulk-upload-progress
 (fn [{db :db} [_ commitment-options]]
   (let [files (private-material-bulk-upload-files db)
         cleanup-mode (get-in db (conj sub/private-material-bulk-upload-path :cleanup-mode))
         upload-files (if (= cleanup-mode :pre-import)
                        (expand-private-material-bulk-upload-files files)
                        files)
         initialized-files (->> upload-files
                                (mapv (fn [{:keys [selected-commitment-id] :as file}]
                                        (assoc file
                                               :status (if (and (= cleanup-mode :pre-import)
                                                                selected-commitment-id)
                                                         :pending
                                                         :skipped)
                                               :percentage 0
                                               :message nil))))
         pending-removals (private-material-bulk-upload-removals
                           files
                           commitment-options
                           (get-in db
                                   (conj sub/private-material-bulk-upload-path
                                         :remove-existing-attachment-ids)))
         total-files (if (= cleanup-mode :pre-import)
                       (count initialized-files)
                       0)
         total-removals (count pending-removals)
         progress-mode (if (pos? total-files)
                         :upload
                         :cleanup-only)]
     (if (zero? (+ total-files total-removals))
       {:fx [[:dispatch [::reset-bulk-upload-after-progress]]]}
       {:db (-> db
                (assoc-in (conj sub/private-material-bulk-upload-path :step) :progress)
                (assoc-in (conj sub/private-material-bulk-upload-path :files) initialized-files)
                (assoc-in (conj sub/private-material-bulk-upload-path :pending-removals) pending-removals)
                (assoc-in (conj sub/private-material-bulk-upload-path :progress-mode) progress-mode)
                (assoc-in (conj sub/private-material-bulk-upload-path :progress-total-files) total-files)
                (assoc-in (conj sub/private-material-bulk-upload-path :progress-total-removals) total-removals)
                (assoc-in (conj sub/private-material-bulk-upload-path :operation-in-flight?) false)
                (assoc-in (conj sub/private-material-bulk-upload-path :cancel-requested?) false)
                (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-error-message) nil)
                (assoc-in (conj sub/private-material-bulk-upload-path :stop-processing-dialog-visible?) false))
        :fx [[:dispatch [::start-next-bulk-upload]]]}))))

(rf/reg-event-fx
 ::start-next-bulk-upload
 (fn [{db :db} _]
   (let [{:keys [feeder? master-report-id pending-removals cancel-requested?
                 stop-processing-dialog-visible? operation-in-flight?]}
         (get-in db sub/private-material-bulk-upload-path)
         next-file (next-private-material-bulk-upload-file
                    (private-material-bulk-upload-files db))]
     (cond
       stop-processing-dialog-visible?
       {}

       operation-in-flight?
       {}

       cancel-requested?
       {:fx [[:dispatch [::complete-bulk-upload master-report-id]]]}

       (seq pending-removals)
       (let [{:keys [commitment-id attachment-id]} (first pending-removals)]
         {:db (assoc-in db
                        (conj sub/private-material-bulk-upload-path :operation-in-flight?)
                        true)
          ::ws/send {:id         :lp-report/remove-private-material-file
                     :body       {:commitment-id commitment-id
                                  :attachment-id attachment-id}
                     :on-success [::bulk-upload-removal-complete attachment-id]
                     :on-error   [::bulk-upload-removal-failed attachment-id]}})

       (not next-file)
       {:fx [[:dispatch [::complete-bulk-upload master-report-id]]]}
       :else
       (let [file-id (:id next-file)
             commitment-id (:selected-commitment-id next-file)
             commitments-path (if feeder?
                                :lp-report-feeder-investor-commitments
                                :lp-report-investor-commitments)
             report-path (if feeder?
                           (conj editor-sub/feeder-reports-path 0)
                           editor-sub/report-path)
             attachments-path [:lp-report-editor
                               commitments-path
                               commitment-id
                               :lp-report-investor-commitment/materials]
             upload-descriptor {:file                      (:file next-file)
                                :owning-entity-id          commitment-id
                                :token                     nil
                                :dispatch-on-upload-failure [::bulk-upload-file-upload-failed
                                                             file-id]}]
         {:db (update-private-material-bulk-upload-file
               (assoc-in db
                         (conj sub/private-material-bulk-upload-path :operation-in-flight?)
                         true)
               file-id
               (fn [file]
                 (assoc file
                        :status :uploading
                        :percentage 0
                        :message nil)))
          :fx [[:dispatch
                [::file-event/upload-file-socket
                 report-path
                 attachments-path
                 [::bulk-upload-file-uploaded file-id commitment-id]
                 [::bulk-upload-progress file-id]
                 upload-descriptor]]]})))))

(rf/reg-event-fx
 ::bulk-upload-removal-complete
 (fn [{db :db} [_ completed-attachment-id _resp]]
   (let [updated-db (-> db
                        (update-in (conj sub/private-material-bulk-upload-path :pending-removals)
                                   (fn [pending-removals]
                                     (->> pending-removals
                                          (remove (fn [{:keys [attachment-id]}]
                                                    (= attachment-id completed-attachment-id)))
                                          (vec))))
                        (assoc-in (conj sub/private-material-bulk-upload-path
                                        :operation-in-flight?)
                                  false))
         advance-fx (advance-private-material-bulk-upload updated-db)]
     {:db updated-db
      :fx (cond-> []
            advance-fx (conj advance-fx))})))

(rf/reg-event-fx
 ::bulk-upload-removal-failed
 (fn [{db :db} [_ _attachment-id error]]
   {:db (-> db
            (assoc-in (conj sub/private-material-bulk-upload-path :step) :cleanup)
            (assoc-in (conj sub/private-material-bulk-upload-path :operation-in-flight?) false)
            (assoc-in (conj sub/private-material-bulk-upload-path :stop-processing-dialog-visible?) false)
            (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-error-message)
                      (private-material-bulk-upload-error-message error)))}))

(rf/reg-event-fx
 ::bulk-upload-progress
 (fn [{db :db} [_ file-id _attachments-ref-path progress]]
   {:db (update-private-material-bulk-upload-file
         db
         file-id
         (fn [file]
           (assoc file
                  :status :uploading
                  :percentage (:percents progress))))}))

(rf/reg-event-fx
 ::bulk-upload-file-uploaded
 (fn [{db :db} [_ file-id commitment-id attachment-id]]
   {:db (update-private-material-bulk-upload-file
         db
         file-id
         (fn [file]
           (assoc file
                  :file nil
                  :status :processing
                  :percentage 100)))
    ::ws/send {:id         :lp-report/add-private-material-file
               :body       {:commitment-id commitment-id
                            :attachment-id attachment-id}
               :on-success [::bulk-upload-file-attached file-id]
               :on-error   [::bulk-upload-file-attach-failed file-id]}}))

(rf/reg-event-fx
 ::bulk-upload-file-upload-failed
 (fn [{db :db} [_ file-id error]]
   (let [updated-db (-> (update-private-material-bulk-upload-file
                         db
                         file-id
                         (fn [file]
                           (assoc file
                                  :file nil
                                  :status :error
                                  :message (:message error))))
                        (assoc-in (conj sub/private-material-bulk-upload-path
                                        :operation-in-flight?)
                                  false))
         advance-fx (advance-private-material-bulk-upload updated-db)]
     {:db updated-db
      :fx (cond-> []
            advance-fx (conj advance-fx))})))

(rf/reg-event-fx
 ::bulk-upload-file-attached
 (fn [{db :db} [_ file-id]]
   (let [updated-db (-> (update-private-material-bulk-upload-file
                         db
                         file-id
                         (fn [file]
                           (assoc file
                                  :status :complete
                                  :message nil)))
                        (assoc-in (conj sub/private-material-bulk-upload-path
                                        :operation-in-flight?)
                                  false))
         advance-fx (advance-private-material-bulk-upload updated-db)]
     {:db updated-db
      :fx (cond-> []
            advance-fx (conj advance-fx))})))

(rf/reg-event-fx
 ::bulk-upload-file-attach-failed
 (fn [{db :db} [_ file-id error]]
   (let [updated-db (-> (update-private-material-bulk-upload-file
                         db
                         file-id
                         (fn [file]
                           (assoc file
                                  :status :error
                                  :message (:message error))))
                        (assoc-in (conj sub/private-material-bulk-upload-path
                                        :operation-in-flight?)
                                  false))
         advance-fx (advance-private-material-bulk-upload updated-db)]
     {:db updated-db
      :fx (cond-> []
            advance-fx (conj advance-fx))})))

(rf/reg-event-fx
 ::reset-bulk-upload-after-progress
 (fn [{db :db} _]
   (let [cleanup-source-step (get-in db (conj sub/private-material-bulk-upload-path :cleanup-source-step))]
     {:db (-> db
              (assoc-in (conj sub/private-material-bulk-upload-path :step)
                        (or cleanup-source-step :upload))
              (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-source-step) nil)
              (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-mode) nil)
              (assoc-in (conj sub/private-material-bulk-upload-path :progress-mode) nil)
              (assoc-in (conj sub/private-material-bulk-upload-path :progress-total-files) 0)
              (assoc-in (conj sub/private-material-bulk-upload-path :progress-total-removals) 0)
              (assoc-in (conj sub/private-material-bulk-upload-path :operation-in-flight?) false)
              (assoc-in (conj sub/private-material-bulk-upload-path :cancel-requested?) false)
              (assoc-in (conj sub/private-material-bulk-upload-path :stop-processing-dialog-visible?) false)
              (assoc-in (conj sub/private-material-bulk-upload-path :cleanup-error-message) nil)
              (assoc-in (conj sub/private-material-bulk-upload-path :remove-existing-attachment-ids) #{})
              (assoc-in (conj sub/private-material-bulk-upload-path :pending-removals) []))})))

(rf/reg-event-fx
 ::complete-bulk-upload
 (fn [{db :db} [_ master-report-id]]
   (let [progress-mode (get-in db (conj sub/private-material-bulk-upload-path :progress-mode))]
     {:fx [[:dispatch
            [::editor-event/re-load-editor-data
             master-report-id
             [(if (= progress-mode :cleanup-only)
                ::reset-bulk-upload-after-progress
                ::close-bulk-upload)]]]]})))

(rf/reg-event-fx
 ::import-failed
 (fn [_ [_ master-report-id on-error error]]
   ;; A failed import is stored like a successful one, so the history panel has
   ;; to refetch; only the success path goes through ::on-data-reloaded.
   {:fx [[:dispatch [::editor-event/load-import-history master-report-id]]
         [:dispatch [::editor-event/lp-fund-report-data-file-update-failed
                     on-error
                     error]]]}))

(def upload-share-of-progress
  "An import is three stages - transfer, processing, done - and the bar shows
  them as thirds. Transferring the file fills the first third."
  33)

(def processing-share-of-progress
  "Processing reports nothing while it runs, so the bar marks that it started
  and waits here."
  66)

(rf/reg-event-fx
 ::process-fund-report-data-only-file
 (fn [{db :db} [_ file report-id master-report-id on-success on-error file-id]]
   (let [updated-files (->> (get-in db sub/uploads-path)
                            (mapv (fn [f]
                                    (if-not (= (:file f) file)
                                      f
                                      (assoc f
                                             :status :processing
                                             :percentage processing-share-of-progress)))))]
     {:db       (assoc-in db sub/uploads-path updated-files)
      ::ws/send {:id         :lp-report/process-fund-data-only
                 :body       {:report-id        report-id
                              :master-report-id master-report-id
                              :attachment-id    file-id}
                 :on-success [::editor-event/re-load-editor-data master-report-id on-success]
                 :on-error   [::import-failed master-report-id on-error]}})))

(rf/reg-event-fx
 ::process-feeder-report-file
 (fn [{db :db} [_ file report-id master-report-id on-success on-error file-id]]
   (let [updated-files (->> (get-in db sub/uploads-path)
                            (mapv (fn [f]
                                    (if-not (= (:file f) file)
                                      f
                                      (assoc f
                                             :status :processing
                                             :percentage processing-share-of-progress)))))]
     {:db       (assoc-in db sub/uploads-path updated-files)
      ::ws/send {:id         :lp-report/process-feeder-data-only
                 :body       {:report-id        report-id
                              :master-report-id master-report-id
                              :attachment-id    file-id}
                 :on-success [::editor-event/re-load-editor-data master-report-id on-success]
                 :on-error   [::import-failed master-report-id on-error]}})))

(rf/reg-event-fx
 ::process-holding-report-file
 (fn [{db :db} [_ file report-id master-report-id on-success on-error file-id]]
   (let [updated-files (->> (get-in db sub/uploads-path)
                            (mapv (fn [f]
                                    (if-not (= (:file f) file)
                                      f
                                      (assoc f
                                             :status :processing
                                             :percentage processing-share-of-progress)))))]
     {:db       (assoc-in db sub/uploads-path updated-files)
      ::ws/send {:id         :lp-report/process-holding-data
                 :body       {:report-id        report-id
                              :master-report-id master-report-id
                              :attachment-id    file-id}
                 :on-success [::editor-event/re-load-editor-data master-report-id on-success]
                 :on-error   [::import-failed master-report-id on-error]}})))

(rf/reg-event-fx
 ::error-upload
 (fn [{db :db} [_ file error]]
   (let [updated-files (->> (get-in db sub/uploads-path)
                            (mapv (fn [f]
                                    (if-not (= (:file f) file)
                                      f
                                      (assoc f
                                             :status :error
                                             ;; Keep the bar where it stopped: a
                                             ;; full bar next to "Error" read as
                                             ;; a finished import.
                                             :percentage (min (or (:percentage f) 0)
                                                              processing-share-of-progress)
                                             ;; Grouped errors render as a list;
                                             ;; the message is the fallback.
                                             :formula-errors (get-in error
                                                                     [:client-friendly-error-data
                                                                      :formula-errors])
                                             :message (:message error))))))]
     {:db (assoc-in db sub/uploads-path updated-files)})))

(rf/reg-event-fx
 ::upload-progress
 (fn [{db :db} [_ file _ref-path progress]]
   (let [updated-files (->> (get-in db sub/uploads-path)
                            (mapv (fn [f]
                                    (if-not (= (:file f) file)
                                      f
                                      (assoc f
                                             :percentage
                                             (-> (:percents progress)
                                                 (* upload-share-of-progress)
                                                 (/ 100)
                                                 (js/Math.round)))))))]
     {:db (assoc-in db sub/uploads-path updated-files)})))

(rf/reg-event-fx
 ::view-holding-report
 (fn [{db :db} [_ holding-report]]
   {:db (-> db
            (assoc-in sub/gathering-mode-path :holding-report-editor)
            (assoc-in sub/gathering-holding-path (:db/id holding-report)))}))

(rf/reg-event-fx
 ::edit-feeder-report
 (fn [{db :db} [_ feeder-report]]
   {:db (-> db
            (assoc-in sub/gathering-mode-path :feeder-report-editor)
            (assoc-in sub/gathering-feeder-path (:db/id feeder-report)))}))

(rf/reg-event-fx
 ::edit-fund-report
 (fn [{db :db} [_ fund-report]]
   {:db (-> db
            (assoc-in sub/gathering-mode-path :fund-report-editor)
            (assoc-in sub/gathering-fund-path (:db/id fund-report)))}))

(rf/reg-event-fx
 ::contact-removed-ok
 (fn [{db :db} [_ investor-id association-id _resp]]
   (let [investor-contacts-path (conj editor-sub/investor-contacts-path investor-id)
         investor-contacts (get-in db investor-contacts-path)
         updated-investor-contacts
         (->> investor-contacts
              (map (fn [{:keys [commitment-id reporting-contacts]}]
                     {:commitment-id      commitment-id
                      :reporting-contacts (dissoc reporting-contacts association-id)}))
              (doall))]
     {:db (-> db
              (assoc-in investor-contacts-path updated-investor-contacts)
              (p/dissoc-in sub/delete-contacts-path))})))

(rf/reg-event-fx
 ::contact-remove-failed
 (fn [{db :db} [_ err]]
   (js/console.log "Remove contact failed : " err)
   {:db (-> db
            (assoc-in sub/delete-contact-error-path err)
            (p/dissoc-in sub/delete-contact-processing-path))}))

(rf/reg-event-fx
 ::remove-reporting-contact
 (fn [{_db :db} [_ investor-id association-id]]
   {::ws/send {:id         :entity/disable!
               :body       {:entity-id association-id}
               :on-success [::contact-removed-ok investor-id association-id]
               :on-error   [::contact-remove-failed]}}))

(rf/reg-event-fx
 ::start-add-contact
 (fn [{db :db} [_ investor-id report-id commitment-id]]
   {:db (assoc-in db sub/add-contacts-path [investor-id report-id commitment-id])}))

(rf/reg-event-fx
 ::start-contact-action
 (fn [{db :db} [_ action investor-id association-id]]
   (case action
     :edit
     {:db (assoc-in db sub/edit-contact-path association-id)}
     :delete
     {:db (assoc-in db sub/delete-contacts-path [association-id investor-id])})))

(rf/reg-event-fx
 ::commitment-creation-processing-done
 (fn [{db :db} [_ investor-name]]
   {:db (p/dissoc-in db (conj sub/add-commitment-to-crm-processing-path investor-name))}))

(rf/reg-event-fx
 ::commitment-created-to-crm-ok
 (fn [{db :db} [_ _report-id investor-name _ext-id _resp]]
   (let [master-report-id (get-in db (conj editor-sub/report-path :db/id))]
     {:fx [[:dispatch
            [::editor-event/re-load-editor-data master-report-id
             [::commitment-creation-processing-done investor-name]]]]})))

(defn open-error-modal!
  [error-message]
  (modals/open-modal!
   error-message/modal-key
   {:title          (loc/tr :attention)
    :body-content   error-message
    :ok-button-text (loc/tr :ok)}))

(rf/reg-event-fx
 ::create-commitment-to-crm-failed
 (fn [{db :db} [_ _report-id investor-name err]]
   (let [{:keys [error message]} err
         msg (cond (string? message)  message
                   (keyword? error)   (loc/tr error)
                   :else              (loc/tr :unknown-error-persists))]
     {:db (-> db
              (p/dissoc-in (conj sub/add-commitment-to-crm-processing-path investor-name))
              (update-in editor-sub/errors-path assoc ::create-commitment-to-crm-failed err))
      :fx [[::modals/open-modal (partial open-error-modal! msg)]]})))

(rf/reg-event-fx
 ::create-commitment-to-crm
 (fn [{db :db} [_ {:keys [report-id investor-name external-id legal-entity-type]}]]
   {:db       (assoc-in db (conj sub/add-commitment-to-crm-processing-path investor-name) true)
    ::ws/send {:id         :lp-report/create-crm-commitment
               :body       {:report-id         report-id
                            :investor-name     investor-name
                            :external-id       external-id
                            :legal-entity-type legal-entity-type}
               :on-success [::commitment-created-to-crm-ok report-id investor-name external-id]
               :on-error   [::create-commitment-to-crm-failed report-id investor-name]}}))

(rf/reg-event-fx
 ::start-commitment-material-upload
 (fn [_ [_ master-report-id commitment-id file feeder?]]
   (let [upload-descriptor {:file             file
                            :owning-entity-id commitment-id
                            :token            nil}
         commitments-path (if feeder?
                            :lp-report-feeder-investor-commitments
                            :lp-report-investor-commitments)
         report-path (if feeder?
                       (conj editor-sub/feeder-reports-path 0)
                       editor-sub/report-path)]
     (rf/dispatch
      [::file-event/upload-file-socket
       report-path
       [:lp-report-editor commitments-path commitment-id :lp-report-investor-commitment/materials]
       [::editor-event/process-commitment-material-upload master-report-id commitment-id]
       nil
       upload-descriptor]))))

(rf/reg-event-fx
 ::remove-private-material-failed
 (fn [{db :db} [_ _commitment-id _attachment-id err]]
   {:db (-> db
            (update-in editor-sub/errors-path assoc ::remove-private-material-failed err))}))

(rf/reg-event-fx
 ::remove-private-material-ok
 (fn [_ [_ master-report-id _commitment-id]]
   {:fx [[:dispatch
          [::editor-event/re-load-editor-data
           master-report-id
           [::editor-event/reload-fund-data-progress master-report-id]]]]}))

(rf/reg-event-fx
 ::remove-private-material
 (fn [_ [_ master-report-id commitment-id attachment-id]]
   {::ws/send {:id         :lp-report/remove-private-material-file
               :body       {:commitment-id commitment-id
                            :attachment-id attachment-id}
               :on-success [::remove-private-material-ok master-report-id commitment-id]
               :on-error   [::remove-private-material-failed commitment-id attachment-id]}}))

(rf/reg-event-fx
 ::download-private-material-ok
 (fn [_ [_ resp]]
   (.open js/window
          (-> resp :body :download-url)
          "_blank")
   nil))

(rf/reg-event-fx
 ::download-private-material-failed
 (fn [_ [_ err]]
   (js/console.error "Download private material failed:" err)
   nil))

(rf/reg-event-fx
 ::download-private-material
 (fn [_ [_ report-id commitment-id attachment-id]]
   {::ws/send {:id         :lp-report/download-private-material-file
               :body       {:report-id     report-id
                            :commitment-id commitment-id
                            :file-id       attachment-id}
               :on-success [::download-private-material-ok]
               :on-error   [::download-private-material-failed]}}))

(rf/reg-event-fx
 ::start-material-file-upload
 (fn [{_db :db} [_ master-report-id report-id file]]
   (let [upload-descriptor {:file  file
                            :owning-entity-id report-id
                            :token nil}]
     (rf/dispatch [::file-event/upload-file-socket
                   editor-sub/report-path
                   [:lp-report-editor :lp-report-attachments]
                   [::editor-event/process-fund-report-material-file master-report-id report-id]
                   nil
                   upload-descriptor]))))

(rf/reg-event-fx
 ::remove-material-failed
 (fn [{db :db} [_ _report-id _attachment-id err]]
   {:db (-> db
            (update-in editor-sub/errors-path assoc ::remove-material-failed err))}))

(rf/reg-event-fx
 ::material-removed-ok
 (fn [{_db :db} [_ report-id _attachment-id]]
   {:fx [[:dispatch
          [::editor-event/re-load-editor-data
           report-id
           [::editor-event/reload-fund-data-progress report-id]]]]}))

(rf/reg-event-fx
 ::remove-material
 (fn [{_db :db} [_ report-id attachment-id]]
   {::ws/send {:id         :lp-report/remove-material
               :body       {:report-id     report-id
                            :attachment-id attachment-id}
               :on-success [::material-removed-ok report-id]
               :on-error   [::remove-material-failed report-id attachment-id]}}))

(rf/reg-event-fx
 ::cancel-adding-concat
 (fn [{db :db} _]
   {:db (p/dissoc-in db sub/add-contacts-path)}))

(rf/reg-event-fx
 ::cancel-contact-edit
 (fn [{db :db} _]
   {:db (p/dissoc-in db sub/edit-contact-path)}))

(rf/reg-event-fx
 ::cancel-remove-contact
 (fn [{db :db} _]
   {:db (p/dissoc-in db sub/delete-contacts-path)}))

(rf/reg-event-fx
 ::contact-created-ok
 (fn [{db :db} [_ _investor-identifier _resp]]
   (let [report-id (get-in db (conj editor-sub/report-path :db/id))]
     {:fx [[:dispatch
            [::editor-event/re-load-editor-data report-id
             [::cancel-adding-concat]]]]})))

(rf/reg-event-fx
 ::contact-create-failed
 (fn [{db :db} [_ err]]
   (js/console.log "Contact create failed : " err)
   {:db (-> db
            (assoc-in sub/add-contact-error-path err)
            (p/dissoc-in sub/add-contact-processing-path))}))

(rf/reg-event-fx
 ::person-updated-ok
 (fn [{db :db} [_ _investor-identifier _resp]]
   (let [report-id (get-in db (conj editor-sub/report-path :db/id))]
     {:fx [[:dispatch
            [::editor-event/re-load-editor-data report-id
             [::cancel-contact-edit]]]]})))

(rf/reg-event-fx
 ::update-person-failed
 (fn [{db :db} [_ err]]
   (js/console.log "Update person failed : " err)
   {:db (-> db
            (assoc-in sub/edit-contact-error-path err)
            (p/dissoc-in sub/edit-contact-processing-path))}))

(rf/reg-event-fx
 ::create-contact
 (fn [{db :db} [_ {:keys [email first-name last-name]}]]
   (let [[investor-identifier report-id commitment-id] (get-in db sub/add-contacts-path)
         message {:id         :lp-report/add-investor-contact
                  :body       {:report-id     report-id
                               :commitment-id commitment-id
                               :email         email
                               :first-name    first-name
                               :last-name     last-name}
                  :on-success [::contact-created-ok investor-identifier]
                  :on-error   [::contact-create-failed]}]
     {::ws/send message
      :db       (assoc-in db
                          [:lp-report-editor :data-gathering :processing-create-contact]
                          true)})))

(rf/reg-event-fx
 ::update-person
 (fn [{db :db} [_ person-id {:keys [email first-name last-name]}]]
   (let [[investor-identifier _report-id _commitment-id] (get-in db sub/add-contacts-path)
         message {:id         :entity/update-attributes
                  :body       {:data  {:person/first-name first-name
                                       :person/last-name  last-name
                                       :person/universal-email-address email}
                               :db/id person-id}
                  :on-success [::person-updated-ok investor-identifier]
                  :on-error   [::update-person-failed]}]
     {::ws/send message
      :db       (assoc-in db sub/edit-contact-processing-path true)})))

(rf/reg-event-fx
 ::submitted-for-review-ok
 (fn [{db :db} [_ _investor-identifier _resp]]
   {:db (-> db
            (assoc-in [:lp-report-editor :lp-report :lp-report/step] :lp-report.step/review))
    :fx [[:dispatch [::editor-event/activate-step :lp-report.step/review]]]}))

(rf/reg-event-fx
 ::submit-for-review-failed
 (fn [{_db :db} [_ err]]
   (js/console.log "Contact create failed : " err)))

(rf/reg-event-fx
 ::submit-cycle-for-review
 (fn [{_db :db} [_ report-id]]
   {::ws/send {:id         :lp-report/submit-cycle-for-review
               :body       {:report-id report-id}
               :on-success [::submitted-for-review-ok report-id]
               :on-error   [::submit-for-review-failed report-id]}}))

(rf/reg-event-fx
 ::download-data-file
 (fn [_ [_ report-id file-id]]
   {:fx [[:dispatch [::editor-event/download-data-file report-id file-id]]]}))

(rf/reg-event-fx
 ::download-material-file-ok
 (fn [{:keys [_db]} [_ resp]]
   (.open js/window
          (-> resp
              :body
              :download-url)
          "_blank")
   nil))

(rf/reg-event-fx
 ::download-material-file-failed
 (fn [{:keys [_db]} [_ err]]
   (js/console.log "Download material file failed:" err)
   nil))

(rf/reg-event-fx
 ::download-material-file
 (fn [_ [_ report-id attachment-id]]
   {::ws/send {:id         :lp-report/download-material-file
               :body       {:report-id     report-id
                            :file-id attachment-id}
               :on-success [::download-material-file-ok]
               :on-error   [::download-material-file-failed]}}))

(rf/reg-event-fx
 ::complete-remaining-texts
 (fn [{db :db} [_ report-id feeder?]]
   (let [report-rich-content-list (if feeder?
                                    (get-in db sub/feeder-rich-content-list-path)
                                    (get-in db sub/rich-content-list-path))
         new-list (->> report-rich-content-list
                       (mapv (fn [rich-content-map]
                               (if-not (:completed? rich-content-map)
                                 (assoc rich-content-map
                                        :completed? true
                                        :rich-content-completed-at (str (c-time/now-millis)))
                                 rich-content-map))))
         complete-paths  (->> report-rich-content-list
                              (keep (fn [rich-content-map]
                                      (when-not (:completed? rich-content-map)
                                        (get rich-content-map :rich-content-completed-path))))
                              (vec))]
     {:db (if feeder?
            (assoc-in db sub/feeder-rich-content-list-path new-list)
            (assoc-in db sub/rich-content-list-path new-list))
      ::ws/send {:id         :lp-report/complete-all-texts
                 :body       {:report-id report-id
                              :complete-paths complete-paths}
                 :on-success [::complete-all-texts-ok feeder?]
                 :on-error   [::complete-all-texts-failed]}})))

(rf/reg-event-fx
 ::complete-all-texts-ok
 (fn [{db :db} [_ feeder? resp]]
   (let [updated-state (-> resp
                           :body)
         new-state (when (string? updated-state)
                     (edn/read-string updated-state))]
     (js/console.log "Completing all texts successful : " resp new-state)
     (when new-state
       {:db (if feeder?
              ;;TODO if we allow fund to have multiple feeders in the future, this needs to be changed
              (assoc-in db [:lp-report-editor :lp-report-feeder-reports 0 :lp-report/editable-data] new-state)
              (assoc-in db [:lp-report-editor :lp-report :lp-report/editable-data] new-state))}))))

(rf/reg-event-fx
 ::complete-all-texts-failed
 (fn [{_db :db} [_ err]]
   (js/console.log "Completing all texts failed : " err)))

(defn init-rich-content-list
  [ui-data]
  (->> (cu/keys-in ui-data)
       (filter (fn [vec]
                 (some #{:rich-content-completed-at} vec)))
       (map butlast)
       (map vec)
       (keep (fn [dynamic-path]
               (let [footnote-key (get-in ui-data (concat dynamic-path [:rich-content-key]))
                     rich-content-completed-path (get-in ui-data (concat dynamic-path [:rich-content-completed-path]))
                     rich-content-completed-at (get-in ui-data (concat dynamic-path [:rich-content-completed-at]))
                     rich-content-path (get-in ui-data (concat dynamic-path [:rich-content-path]))
                     rich-content (get-in ui-data (concat dynamic-path [:rich-content]))]
                 (when footnote-key {:footnote-key footnote-key
                                     :rich-content-completed-path rich-content-completed-path
                                     :rich-content-completed-at rich-content-completed-at
                                     :rich-content-path rich-content-path
                                     :rich-content rich-content
                                     :completed? (not (cu/blank-value? rich-content-completed-at))}))))
       (vec)))

(rf/reg-event-fx
 ::init-rich-content-list
 (fn [{db :db} [_ ui-data feeder-ui-data ui-data-by-holding]]
   {:db (cond-> db
          ui-data (assoc-in sub/rich-content-list-path (init-rich-content-list ui-data))
          feeder-ui-data (assoc-in sub/feeder-rich-content-list-path (init-rich-content-list feeder-ui-data))
          ui-data-by-holding (assoc-in sub/holdings-rich-content-list-path (->> ui-data-by-holding
                                                                                (map (fn [[id holding-ui-data]]
                                                                                       [id (init-rich-content-list holding-ui-data)]))
                                                                                (into {}))))}))

(defn set-report-attr
  "Sets attr on the report matching report-id (the main report or a feeder) in
  app-db."
  [db report-id attr value]
  (if (= report-id (get-in db (conj editor-sub/report-path :db/id)))
    (assoc-in db (conj editor-sub/report-path attr) value)
    (update-in db editor-sub/feeder-reports-path
               (fn [feeders]
                 (mapv (fn [feeder]
                         (if (= (:db/id feeder) report-id)
                           (assoc feeder attr value)
                           feeder))
                       feeders)))))

(rf/reg-event-fx
 ::save-export-permissions
 (fn [_ [_ {:keys [report-id permissions]}]]
   {::ws/send {:id         :lp-report/update-export-permissions
               :body       {:report-id   report-id
                            :permissions permissions}
               :on-success [::export-permissions-saved report-id]
               :on-error   [::export-permissions-save-failed report-id]}}))

(rf/reg-event-db
 ::export-permissions-saved
 (fn [db [_ report-id response]]
   (set-report-attr db report-id :lp-report/export-permissions
                    (get-in response [:body :permissions]))))

(rf/reg-event-fx
 ::export-permissions-save-failed
 (fn [_ [_ _report-id err]]
   (let [{:keys [error message]} err
         msg (cond (string? message)  message
                   (keyword? error)   (loc/tr error)
                   :else              (loc/tr :unknown-error-persists))]
     {:fx [[::modals/open-modal (partial open-error-modal! msg)]]})))

(rf/reg-event-db
 ::set-materials-selected-tab
 (fn [db [_ report-id tab]]
   (assoc-in db [:materials-selected-tab report-id] tab)))
